import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import type { ClashRecord, DigOnceEntry } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const UTILITY_TYPES = ['WATER', 'ELECTRICITY', 'TELECOM', 'SEWERAGE', 'ROAD', 'DRAINAGE', 'OTHER'] as const;
const STATUSES = ['PLANNED', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
const ALLOWED_NEXT: Record<string, string[]> = {
  PLANNED: ['CONFIRMED', 'CANCELLED'],
  CONFIRMED: ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['COMPLETED'],
  COMPLETED: [],
  CANCELLED: [],
};

export function DigOnceRegisterList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showClash, setShowClash] = useState(false);

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (status) params.set('status', status);
    if (q.trim()) params.set('q', q.trim());
    return `/api/v1/m20/dig-once?${params.toString()}`;
  }, [status, q]);
  const list = useCursorList<DigOnceEntry>(basePath);

  return (
    <div>
      <PageHeader
        title="Dig-Once Register"
        subtitle="Planned utility/road works by corridor — the coordination board every works permit is clash-checked against."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setShowClash(true)}>Check clashes</button>
            <button className="btn-primary" onClick={() => setShowNew(true)}>+ Register works</button>
          </>
        }
      />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status" className="w-48">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Search" className="min-w-[200px] flex-1">
          <input className="field-input" placeholder="Corridor, agency, ref no" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[700px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Ref no</th>
              <th className="table-head">Corridor</th>
              <th className="table-head">District</th>
              <th className="table-head">Utility</th>
              <th className="table-head">Agency</th>
              <th className="table-head">Status</th>
              <th className="table-head">Planned dates</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((d) => (
              <tr key={d.id} className="table-row-link" onClick={() => navigate(`/infrastructure/dig-once/${d.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{d.refNo}</td>
                <td className="table-cell">{d.corridor}</td>
                <td className="table-cell">{d.district}</td>
                <td className="table-cell text-xs">{d.utilityType}</td>
                <td className="table-cell text-xs">{d.agencyName}</td>
                <td className="table-cell"><StatusBadge status={d.status} /></td>
                <td className="table-cell text-xs text-slate-500">{formatDate(d.plannedStartDate)} – {formatDate(d.plannedEndDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading register…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No dig-once entries found" /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />

      {showNew ? (
        <NewEntryModal onClose={() => setShowNew(false)} onCreated={(d) => { setShowNew(false); navigate(`/infrastructure/dig-once/${d.id}`); }} />
      ) : null}
      {showClash ? <ClashLookupModal onClose={() => setShowClash(false)} /> : null}
    </div>
  );
}

function NewEntryModal({ onClose, onCreated }: { onClose: () => void; onCreated: (d: DigOnceEntry) => void }) {
  const [corridor, setCorridor] = useState('');
  const [district, setDistrict] = useState('');
  const [utilityType, setUtilityType] = useState<(typeof UTILITY_TYPES)[number]>('WATER');
  const [agencyName, setAgencyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [description, setDescription] = useState('');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<DigOnceEntry>('/api/v1/m20/dig-once', {
        corridor,
        district,
        utilityType,
        agencyName,
        contactName: contactName || undefined,
        contactPhone: contactPhone || undefined,
        description: description || undefined,
        plannedStartDate,
        plannedEndDate,
      });
      onCreated(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not register works');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register planned works" onClose={onClose} wide>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Corridor">
          <input className="field-input" value={corridor} onChange={(e) => setCorridor(e.target.value)} placeholder="e.g. Bel Ombre Road" />
        </Field>
        <Field label="District">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <Field label="Utility type">
          <select className="field-input" value={utilityType} onChange={(e) => setUtilityType(e.target.value as typeof utilityType)}>
            {UTILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Agency name">
          <input className="field-input" value={agencyName} onChange={(e) => setAgencyName(e.target.value)} placeholder="e.g. Public Utilities Corporation" />
        </Field>
        <Field label="Contact name">
          <input className="field-input" value={contactName} onChange={(e) => setContactName(e.target.value)} />
        </Field>
        <Field label="Contact phone">
          <input className="field-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
        </Field>
        <Field label="Planned start date">
          <input className="field-input" type="date" value={plannedStartDate} onChange={(e) => setPlannedStartDate(e.target.value)} />
        </Field>
        <Field label="Planned end date">
          <input className="field-input" type="date" value={plannedEndDate} onChange={(e) => setPlannedEndDate(e.target.value)} />
        </Field>
        <Field label="Description" className="sm:col-span-2">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !corridor || !district || !agencyName || !plannedStartDate || !plannedEndDate} onClick={submit}>
          {busy ? 'Registering…' : 'Register'}
        </button>
      </div>
    </Modal>
  );
}

function ClashLookupModal({ onClose }: { onClose: () => void }) {
  const [corridor, setCorridor] = useState('');
  const [district, setDistrict] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clashes, setClashes] = useState<ClashRecord[] | null>(null);

  const check = async () => {
    setBusy(true);
    setError(null);
    setClashes(null);
    try {
      const params = new URLSearchParams({ corridor, district, from, to });
      const res = await api.get<{ items: ClashRecord[] }>(`/api/v1/m20/dig-once/clashes?${params.toString()}`);
      setClashes(res.items);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Ad hoc clash lookup" onClose={onClose} wide>
      <p className="mb-3 text-xs text-slate-500">GET /api/v1/m20/dig-once/clashes — checks both the dig-once register and other in-flight works permits.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Corridor"><input className="field-input" value={corridor} onChange={(e) => setCorridor(e.target.value)} /></Field>
        <Field label="District"><input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} /></Field>
        <Field label="From"><input className="field-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input className="field-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </div>
      <button className="btn-primary mt-3" disabled={busy || !corridor || !district || !from || !to} onClick={check}>
        {busy ? 'Checking…' : 'Check for clashes'}
      </button>
      {clashes ? (
        clashes.length === 0 ? (
          <p className="mt-3 text-sm font-semibold text-emerald-600">No clashes found.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-sm">
            {clashes.map((c, i) => (
              <li key={i} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700">
                {c.source} {c.refNo} — {c.status} ({formatDate(c.plannedStartDate)} – {formatDate(c.plannedEndDate)})
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Modal>
  );
}

export function DigOnceDetail() {
  const { id = '' } = useParams();
  const entryQ = useApiQuery<DigOnceEntry>(`/api/v1/m20/dig-once/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transition = async (status: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m20/dig-once/${id}/status`, { status });
      entryQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Transition failed');
    } finally {
      setBusy(false);
    }
  };

  if (entryQ.loading) return <Spinner label="Loading entry…" />;
  if (entryQ.error || !entryQ.data) return <ErrorNote message={entryQ.error ?? 'Entry not found'} />;
  const d = entryQ.data;
  const next = ALLOWED_NEXT[d.status] ?? [];

  return (
    <div>
      <BackLink to="/infrastructure/dig-once" />
      <PageHeader title={d.refNo} subtitle={`${d.corridor}, ${d.district}`} actions={<StatusBadge status={d.status} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="card p-5">
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-slate-500">Utility type</dt><dd className="font-medium text-slate-800">{d.utilityType}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Agency</dt><dd className="font-medium text-slate-800">{d.agencyName}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Contact</dt><dd className="font-medium text-slate-800">{d.contactName ?? '—'} {d.contactPhone ? `(${d.contactPhone})` : ''}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Planned dates</dt><dd className="font-medium text-slate-800">{formatDate(d.plannedStartDate)} – {formatDate(d.plannedEndDate)}</dd></div>
        </dl>
        {d.description ? <p className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">{d.description}</p> : null}
        {next.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {next.map((s) => (
              <button key={s} className="btn-secondary" disabled={busy} onClick={() => transition(s)}>
                Move to {s.replaceAll('_', ' ')}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
